import { Plus } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { FormProvider, useFieldArray, useFormContext, useWatch } from 'react-hook-form'

import { useAllowances } from 'apps/employer_renewals/providers/AllowancesProvider'
import { useEmployer } from 'apps/employer_renewals/providers/EmployerProvider'
import AllowanceClass from 'components/quoting/quotes/flat_allowances/AllowanceClass'
import { type Allowance, type Allowances, type AllowanceWithIndex, getDefaultAllowance } from 'components/quoting/quotes/flat_allowances/FlatAllowanceExplorer'
import { getFullName } from 'components/reusable/form/StateDropdown'
import { Button } from 'slab'
import { formatMoney } from 'utils'
import { type PostQuotingProspectsProspectIdQuotesQuoteIdAllowancesAllowancesItem as UpdateAllowanceItem } from 'utils/web_api.schemas'

import DefaultAllowance from './DefaultAllowance'
import FlatAllowanceApproachSelect from './FlatAllowanceApproachSelect'

export default function FlatAllowanceConfigurator () {
  const {
    renewalYearAllowanceParams,
    setRenewalYearAllowanceParams,
    flatAllowanceApproach,
    setFlatAllowanceApproach,
    minimumContributions,
  } = useAllowances()
  const { employerIsALE } = useEmployer()

  const [allowanceAddedAt, setAllowanceAddedAt] = useState<number>()
  const isDirtyRef = useRef(false)

  const methods = useFormContext<Allowances>()

  const { fields: allowanceFields, append: appendAllowance, remove: removeAllowance } = useFieldArray({
    control: methods.control,
    name: 'allowances',
  })

  const formAllowances = useWatch({
    control: methods.control,
    name: 'allowances',
  })

  const defaultAllowanceIndex = useMemo(() => {
    return allowanceFields.findIndex(allowance => !allowance.class)
  }, [allowanceFields])

  const sortedClassAllowances: AllowanceWithIndex[] = useMemo(() => (
    allowanceFields
      .map((field, index) => ({
        ...field,
        index,
      }))
      .filter(allowance => allowance.class)
      .sort((a, b) => (getFullName(a.class?.location_id || '') || 'Z').localeCompare(getFullName(b.class?.location_id || '') || 'Z'))
  ), [allowanceFields, allowanceAddedAt])

  // Set the default form state
  useEffect(() => {
    if (formAllowances.length > 0 || renewalYearAllowanceParams.length === 0) {
      return
    }

    methods.reset({ allowances: renewalYearAllowanceParams })
    isDirtyRef.current = false
  }, [renewalYearAllowanceParams, methods])

  // Track user interaction with the form
  useEffect(() => {
    if (methods.formState.isDirty) {
      isDirtyRef.current = true
    } else {
      // When the form is reset (isDirty becomes false), we need to manually trigger the validation after a short delay
      // to ensure the form is fully reset before triggering the validation
      const timeout = setTimeout(() => {
        methods.trigger()
      }, 200)

      return () => clearTimeout(timeout)
    }
  }, [methods.formState.isDirty])

  // Update the renewal year allowance params when a user updates the form
  useEffect(() => {
    if (formAllowances.length === 0 || !isDirtyRef.current) {
      return
    }

    setRenewalYearAllowanceParams(formAllowances as UpdateAllowanceItem[])
  }, [formAllowances, setRenewalYearAllowanceParams])

  // Update the form state when the flat allowance approach changes
  useEffect(() => {
    if (!flatAllowanceApproach) {
      return
    }

    if (renewalYearAllowanceParams) {
      methods.reset({ allowances: renewalYearAllowanceParams })
      isDirtyRef.current = false

      setTimeout(() => {
        methods.trigger()
      }, 0)
    }
  }, [flatAllowanceApproach])

  const validateEmployeeAllowance = useCallback((value: number, allowance: Allowance) => {
    const allowanceState = allowance.class?.location_id

    let minContribution: number | undefined
    if (allowanceState) {
      minContribution = minimumContributions?.minContributionByState?.[allowanceState]
    } else {
      minContribution = minimumContributions?.minDefaultContribution
    }

    if (minContribution && value < minContribution) {
      if (employerIsALE) {
        return `The employee allowance must be a minimum of ${formatMoney(minContribution)} to be considered affordable`
      }

      return `An allowance below ${formatMoney(minContribution)} may be unaffordable for some employees`
    }
  }, [minimumContributions, employerIsALE])

  return (
    <FormProvider {...methods}>
      <div className='flex flex-col gap-6'>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-row items-center justify-between gap-2'>
            <div className='body text-gray-500'>Edit default monthly allowance</div>
            {flatAllowanceApproach &&
              <FlatAllowanceApproachSelect
                flatAllowanceApproach={flatAllowanceApproach}
                onFlatAllowanceApproachChange={setFlatAllowanceApproach}
              />}
          </div>
          <div className='body-sm text-gray-500'>
            We recommend updating your allowances to account for changes in premium costs for 2026. This ensures your team retains the same buying power and access to plans as they had in 2025.
          </div>
          <DefaultAllowance
            formIndex={defaultAllowanceIndex}
            isLoading={defaultAllowanceIndex === -1}
            validateEmployeeAllowance={validateEmployeeAllowance}
          />
        </div>
        <div className='flex flex-col gap-4'>
          <div className='flex flex-row items-center justify-between'>
            <div className='body text-gray-500'>Custom state-based allowances</div>
            <Button
              variant='tertiary'
              leftIcon={Plus}
              color='purple'
              onClick={() => appendAllowance({ class: { location_type: 'state' }, ...getDefaultAllowance() })}
            >Add state allowance
            </Button>
          </div>
          <div className='flex flex-col gap-2'>
            {sortedClassAllowances.length > 0 &&
              sortedClassAllowances.map((allowance) => (
                <AllowanceClass
                  key={allowance.index}
                  index={allowance.index}
                  defaultOpen={!allowance.class?.location_id}
                  onAdd={() => setAllowanceAddedAt(Date.now())}
                  onRemove={() => removeAllowance(allowance.index)}
                  validateEmployeeAllowance={validateEmployeeAllowance}
                  bypassEmployeeAllownaceErrorForALEs
                />
              ))}
          </div>
        </div>
      </div>
    </FormProvider>
  )
}
